/**
 * Done. / ChiroCPD — Button Primitive
 *
 * WCAG 2.1 AA: minimum 44px touch target, aria-disabled pattern (not disabled),
 * keyboard-accessible, visible focus ring.
 *
 * aria-disabled pattern: button remains focusable when disabled so screen readers
 * can announce the reason (e.g. "Subscription expired").
 */

import type { ReactNode } from 'react';
import { cx } from '@/lib/utils';
import type { ButtonVariant, ButtonSize } from '@/lib/types';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Use aria-disabled pattern instead of HTML disabled to keep element focusable */
  ariaDisabled?: boolean;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--color-done-600)] text-white hover:bg-[var(--color-done-700)] active:bg-[var(--color-done-800)]',
  secondary:
    'bg-white text-[var(--color-done-700)] border border-[var(--color-done-600)] hover:bg-[var(--color-done-50)] active:bg-[var(--color-done-100)]',
  ghost:
    'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--surface-muted)] active:bg-[var(--border-base)]',
  danger:
    'bg-[var(--color-error-500)] text-white hover:bg-red-600 active:bg-red-700',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm min-h-[44px]',
  md: 'px-4 py-2 text-sm min-h-[44px]',
  lg: 'px-6 py-3 text-base min-h-[44px]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  ariaDisabled = false,
  ariaDescribedBy,
  ariaLabel,
  children,
  onClick,
  type = 'button',
  className,
}: ButtonProps): React.ReactElement {
  function handleClick(): void {
    if (ariaDisabled) return; // No-op guard for aria-disabled pattern
    onClick?.();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
    if (ariaDisabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
    }
  }

  return (
    <button
      type={type}
      aria-disabled={ariaDisabled || undefined}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-[var(--radius-md)] font-medium',
        'transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-[var(--color-done-500)] focus-visible:ring-offset-2',
        'select-none cursor-pointer',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        ariaDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        className,
      )}
    >
      {children}
    </button>
  );
}
